// The scripted week: the two formats that replaced the verse hook and the
// evening story on 2026-11-02, written ahead and recorded by the operator.
//
//   MORNING · "Can you answer this?"   (kind `challenge`)
//     He asks the question in his own voice, the four options are on screen,
//     the clock runs, and he gives the answer and its teach line at the
//     reveal. Questions come from the app's own trivia data
//     (src/data/bible/trivia.ts), so the post teaches exactly what the game
//     teaches.
//
//   EVENING · "Most people get this wrong"   (kind `moment`)
//     A common belief, what the text actually says, and a question back to
//     the viewer — his voice over one held painting. Narrative facts only,
//     the trivia rule: nothing one tradition answers differently.
//
// Why these two. 94 posts (Oct 11–31) drew ZERO real comments and about five
// shares: the only comment under each was our own link. Both formats END ON A
// QUESTION THE VIEWER CAN ANSWER IN ONE WORD, and the caption asks it — that
// is the whole point of them. See docs/scripts/week-2026-11-02.md for what he
// reads.
//
// Keyed by the date the post GOES OUT. A date with no entry here is the
// engine exactly as it was (verse + story), so a week nobody scripted falls
// back rather than going dark.
//
// Pure data, no imports: the morning runner reads it in Node to decide the
// day's kinds, and the comment replier reads `scriptedQuiz` to know the
// answer it is replying about.

export interface ScriptedQuiz {
  /** On-screen opening line, max ~8 words. */
  hook: string
  prompt: string
  /** Four options, answer already shuffled into place. */
  options: [string, string, string, string]
  answerIndex: number
  /** Shown at the reveal under the answer. */
  teach: string
  /** Where it is in the text — the end card names it. */
  reference: string
  /** The question the caption and the first comment ask. */
  ask: string
  /** YouTube title (search intent, under 70 chars). */
  title: string
  /** Caption body, before the ask. Plain, no links, no emoji. */
  caption: string
  tags: string[]
}

export interface ScriptedMyth {
  hook: string
  /** The end card's title. */
  title: string
  reference: string
  /** A root-relative painting held for the whole post. */
  art: string
  ask: string
  title_yt: string
  caption: string
  tags: string[]
}

export const SCRIPTED_QUIZ: Record<string, ScriptedQuiz> = {
  '2026-11-01': {
    hook: 'The walls fell on the seventh day.',
    prompt: 'On the seventh day, how many times did Israel march around Jericho?',
    options: ['Once', 'Three', 'Seven', 'Twelve'],
    answerIndex: 2,
    teach: 'Once a day for six days, seven times on the seventh, and the wall fell flat.',
    reference: 'Joshua 6:15',
    ask: 'Did you get it?',
    title: 'How many times did Israel march around Jericho? Bible quiz',
    caption: 'Six days of silence, then seven laps on the last one.',
    tags: ['jericho', 'joshua', 'biblequiz', 'bibletrivia', 'shorts'],
  },
  '2026-11-02': {
    hook: 'Almost nobody knows how Jonah ends.',
    prompt: 'How does the book of Jonah end?',
    options: ['With Jonah preaching again', 'With the city destroyed', 'With a question from God that is never answered', 'With Jonah returning home'],
    answerIndex: 2,
    teach: '"Should I not have compassion on that great city?" The book stops there.',
    reference: 'Jonah 4:11',
    ask: 'Did you know how it ends?',
    title: 'How does the book of Jonah end? Most people don’t know',
    caption: 'Everybody remembers the fish. Almost nobody remembers the last line.',
    tags: ['jonah', 'biblequiz', 'bibletrivia', 'bible', 'shorts'],
  },
  '2026-11-03': {
    hook: 'God made Gideon’s army smaller on purpose.',
    prompt: 'With how many men did Gideon defeat the Midianites?',
    options: ['Ten thousand', 'Three hundred', 'Thirty', 'Seven hundred'],
    answerIndex: 1,
    teach: 'He started with 32,000. They won with torches, jars and trumpets.',
    reference: 'Judges 7:7',
    ask: 'What did you guess?',
    title: 'How many men did Gideon fight with? Bible quiz',
    caption: 'God kept cutting the army down until nobody could say they saved themselves.',
    tags: ['gideon', 'biblequiz', 'bibletrivia', 'judges', 'shorts'],
  },
  '2026-11-04': {
    hook: 'Jacob married the wrong sister.',
    prompt: 'Jacob worked seven years for Rachel. Who was he given instead?',
    options: ['Dinah', 'Rebekah', 'Bilhah', 'Leah'],
    answerIndex: 3,
    teach: 'Laban swapped them on the wedding night. Jacob worked seven more years for Rachel.',
    reference: 'Genesis 29:25',
    ask: 'Did you get it?',
    title: 'Who did Jacob marry by mistake? Bible quiz',
    caption: 'Seven years of work, and he woke up married to her sister.',
    tags: ['jacob', 'biblequiz', 'genesis', 'bibletrivia', 'shorts'],
  },
  '2026-11-05': {
    hook: 'A foreign widow became a king’s great-grandmother.',
    prompt: 'Ruth’s great-grandson became one of the most famous people in the Bible. Who?',
    options: ['King David', 'King Saul', 'Solomon', 'Samuel'],
    answerIndex: 0,
    teach: 'Ruth → Obed → Jesse → David.',
    reference: 'Ruth 4:17',
    ask: 'Did you know that one?',
    title: 'Who was Ruth’s great-grandson? Bible quiz',
    caption: 'A widow from Moab, picking up what the harvesters left behind.',
    tags: ['ruth', 'biblequiz', 'kingdavid', 'bibletrivia', 'shorts'],
  },
  '2026-11-06': {
    hook: 'The shortest verse in the Bible.',
    prompt: 'What is the shortest verse in most English Bibles?',
    options: ['God is love', 'Pray without ceasing', 'Rejoice always', 'Jesus wept'],
    answerIndex: 3,
    teach: 'John 11:35, at Lazarus’ tomb. He knew He was about to raise him. He wept anyway.',
    reference: 'John 11:35',
    ask: 'Did you get it?',
    title: 'What is the shortest verse in the Bible?',
    caption: 'Two words, at the tomb of a friend He was about to raise.',
    tags: ['jesuswept', 'biblequiz', 'john', 'bibletrivia', 'shorts'],
  },
  '2026-11-07': {
    hook: 'One woman inside Jericho changed sides.',
    prompt: 'Which woman of Jericho hid the two spies on her roof?',
    options: ['Deborah', 'Rahab', 'Jael', 'Miriam'],
    answerIndex: 1,
    teach: 'Her whole family was spared. Matthew puts her in the family line of Jesus.',
    reference: 'Joshua 2:6',
    ask: 'Did you know that?',
    title: 'Who hid the spies in Jericho? Bible quiz',
    caption: 'She hid them under stalks of flax, and ended up in Jesus’ family tree.',
    tags: ['rahab', 'biblequiz', 'joshua', 'bibletrivia', 'shorts'],
  },
  '2026-11-08': {
    hook: 'Luke chose who heard first.',
    prompt: 'Who does Luke say were the first people told that Jesus was born?',
    options: ['Wise men from the east', 'Priests in the temple', 'Shepherds in the fields', 'Herod’s household'],
    answerIndex: 2,
    teach: 'Night-shift workers, not priests and not kings. Luke does this on purpose.',
    reference: 'Luke 2:8',
    ask: 'What did you say?',
    title: 'Who heard about Jesus’ birth first? Bible quiz',
    caption: 'Not priests, not kings. The men on the night shift.',
    tags: ['christmas', 'biblequiz', 'luke', 'bibletrivia', 'shorts'],
  },
  '2026-11-09': {
    hook: "Food stopped a war.",
    prompt: "Which woman stopped David’s revenge with nothing but food?",
    options: ["Michal", "Bathsheba", "Abigail", "Merab"],
    answerIndex: 2,
    teach: "She rode out with loaves, wine and figs and talked him out of it.",
    reference: "1 Samuel 25:18",
    ask: "Did you get it?",
    title: "Who stopped David’s revenge with food? Bible quiz",
    caption: "David was on his way to take revenge. She met him on the road with bread.",
    tags: ["abigail", "biblequiz", "kingdavid", "bibletrivia", "shorts"],
  },
  '2026-11-10': {
    hook: "The general hid in the wrong tent.",
    prompt: "Who killed the commander Sisera with a tent peg?",
    options: ["Deborah", "Jael", "Barak", "Abimelech"],
    answerIndex: 1,
    teach: "He fled to a tent he thought was friendly. Deborah sings about it in Judges 5.",
    reference: "Judges 4:21",
    ask: "Did you know that one?",
    title: "Who took down Sisera? Bible quiz",
    caption: "An enemy general ran from battle and hid in the wrong tent.",
    tags: ["jael", "biblequiz", "judges", "bibletrivia", "shorts"],
  },
  '2026-11-11': {
    hook: "He came at night so nobody would see.",
    prompt: "Which religious leader came to Jesus by night?",
    options: ["Gamaliel", "Joseph of Arimathea", "Simon", "Nicodemus"],
    answerIndex: 3,
    teach: "He shows up twice more. The last time, he brings spices to the tomb.",
    reference: "John 3:2",
    ask: "Did you get it?",
    title: "Who came to Jesus at night? Bible quiz",
    caption: "He came in the dark. He came back in daylight, to the tomb.",
    tags: ["nicodemus", "biblequiz", "john", "bibletrivia", "shorts"],
  },
  '2026-11-12': {
    hook: "The first to die for following Jesus.",
    prompt: "Who was the first follower of Jesus to die for it?",
    options: ["Stephen", "James", "Peter", "Barnabas"],
    answerIndex: 0,
    teach: "A young man named Saul stood there holding their coats. That’s Paul.",
    reference: "Acts 7:58",
    ask: "Did you know that?",
    title: "Who was the first Christian martyr? Bible quiz",
    caption: "Watch who is holding the coats.",
    tags: ["stephen", "biblequiz", "acts", "bibletrivia", "shorts"],
  },
  '2026-11-13': {
    hook: "He kept running to the wrong person.",
    prompt: "Which boy heard God call his name three times in the night?",
    options: ["David", "Saul", "Samuel", "Jonathan"],
    answerIndex: 2,
    teach: "He thought it was Eli. The third time he said, \"Speak, for your servant is listening.\"",
    reference: "1 Samuel 3:10",
    ask: "What did you guess?",
    title: "Which boy heard God call him at night? Bible quiz",
    caption: "Three times he got up and went to the wrong room.",
    tags: ["samuel", "biblequiz", "bibletrivia", "prayer", "shorts"],
  },
  '2026-11-14': {
    hook: "Esther was an orphan.",
    prompt: "Who raised Esther after her parents died?",
    options: ["Haman", "Mordecai", "Hegai", "Memucan"],
    answerIndex: 1,
    teach: "Her cousin. Later he tells her she may be there \"for such a time as this.\"",
    reference: "Esther 2:7",
    ask: "Did you get it?",
    title: "Who raised Esther? Bible quiz",
    caption: "The man who raised her is the one who sends her in.",
    tags: ["esther", "biblequiz", "mordecai", "bibletrivia", "shorts"],
  },
  '2026-11-15': {
    hook: "Gideon asked for a sign twice.",
    prompt: "What did Gideon use to test whether God had really spoken?",
    options: ["A stone altar", "A grain offering", "A clay jar", "A wool fleece"],
    answerIndex: 3,
    teach: "Wet with the ground dry, then dry with the ground wet. God answered both.",
    reference: "Judges 6:37",
    ask: "Did you know that one?",
    title: "What did Gideon test God with? Bible quiz",
    caption: "He asked twice. He got an answer twice.",
    tags: ["gideon", "biblequiz", "judges", "bibletrivia", "shorts"],
  },
  '2026-11-16': {
    hook: "Jonah went the opposite way.",
    prompt: "God sent Jonah to Nineveh. Where did Jonah try to sail instead?",
    options: ["Egypt", "Cyprus", "Tarshish", "Joppa"],
    answerIndex: 2,
    teach: "As far the other way as a ship could go. He slept below deck when the storm hit.",
    reference: "Jonah 1:3",
    ask: "Did you get it?",
    title: "Where did Jonah run to? Bible quiz",
    caption: "He bought a ticket for the other end of the world.",
    tags: ["jonah", "biblequiz", "bibletrivia", "bible", "shorts"],
  },
  '2026-11-17': {
    hook: "His longest recorded conversation.",
    prompt: "Jesus’ longest recorded conversation was with a woman at a well. Where?",
    options: ["Samaria", "Bethany", "Nazareth", "Jericho"],
    answerIndex: 0,
    teach: "She went back and told the whole town.",
    reference: "John 4:7",
    ask: "Did you know that?",
    title: "Where was the woman at the well? Bible quiz",
    caption: "A well at noon, and the longest conversation in the gospels.",
    tags: ["womanatthewell", "biblequiz", "john", "bibletrivia", "shorts"],
  },
  '2026-11-18': {
    hook: "Jesus called him from his booth.",
    prompt: "What was Matthew’s job when Jesus called him?",
    options: ["Fisherman", "Carpenter", "Tax collector", "Tentmaker"],
    answerIndex: 2,
    teach: "He left the booth. Next scene: a dinner full of the wrong sort of people.",
    reference: "Matthew 9:9",
    ask: "Did you get it?",
    title: "What was Matthew’s job? Bible quiz",
    caption: "He got up and left the money on the table.",
    tags: ["matthew", "biblequiz", "bibletrivia", "bible", "shorts"],
  },
  '2026-11-19': {
    hook: "Not Jerusalem.",
    prompt: "In which city were the disciples first called Christians?",
    options: ["Jerusalem", "Antioch", "Rome", "Corinth"],
    answerIndex: 1,
    teach: "It seems to have started as a nickname from outsiders.",
    reference: "Acts 11:26",
    ask: "What did you guess?",
    title: "Where were Christians first called Christians? Bible quiz",
    caption: "The name came from people watching them.",
    tags: ["antioch", "biblequiz", "acts", "bibletrivia", "shorts"],
  },
  '2026-11-20': {
    hook: "She travelled just to ask questions.",
    prompt: "Which queen travelled to test Solomon with hard questions?",
    options: ["Jezebel", "Athaliah", "Esther", "The Queen of Sheba"],
    answerIndex: 3,
    teach: "She brought gold and spices and said the half had not been told her.",
    reference: "1 Kings 10:1",
    ask: "Did you know that one?",
    title: "Who tested Solomon with hard questions? Bible quiz",
    caption: "She came a long way with a list of questions.",
    tags: ["solomon", "queenofsheba", "biblequiz", "bibletrivia", "shorts"],
  },
  '2026-11-21': {
    hook: "One language became many.",
    prompt: "At which tower did one language become many?",
    options: ["Jericho", "Babel", "Siloam", "Shechem"],
    answerIndex: 1,
    teach: "They built it to reach the heavens and make a name for themselves.",
    reference: "Genesis 11:9",
    ask: "Did you get it?",
    title: "Which tower confused the languages? Bible quiz",
    caption: "They wanted a name for themselves. They got a word for confusion.",
    tags: ["babel", "biblequiz", "genesis", "bibletrivia", "shorts"],
  },
  '2026-11-22': {
    hook: "Meanwhile, at the bottom of the mountain.",
    prompt: "While Moses was on the mountain, what did the people make?",
    options: ["A bronze serpent", "A golden calf", "A carved eagle", "A silver throne"],
    answerIndex: 1,
    teach: "Out of their own earrings. Moses came down and broke the tablets.",
    reference: "Exodus 32:4",
    ask: "Did you know that?",
    title: "What did Israel make while Moses was away? Bible quiz",
    caption: "Forty days was too long to wait.",
    tags: ["goldencalf", "moses", "biblequiz", "bibletrivia", "shorts"],
  },
  '2026-11-23': {
    hook: "The priest thought she was drunk.",
    prompt: "Who prayed for a son so silently that the priest thought she was drunk?",
    options: ["Hannah", "Peninnah", "Naomi", "Abigail"],
    answerIndex: 0,
    teach: "Her son was Samuel, and she gave him back to God.",
    reference: "1 Samuel 1:20",
    ask: "Did you get it?",
    title: "Who prayed so hard the priest thought she was drunk?",
    caption: "Her lips moved and no sound came out.",
    tags: ["hannah", "biblequiz", "prayer", "bibletrivia", "shorts"],
  },
  '2026-11-24': {
    hook: "Luke had a day job.",
    prompt: "What was Luke’s job?",
    options: ["A tentmaker", "A tax collector", "A physician", "A fisherman"],
    answerIndex: 2,
    teach: "Paul calls him \"the beloved physician.\" He wrote Luke and Acts.",
    reference: "Colossians 4:14",
    ask: "Did you know that?",
    title: "What was Luke’s job? Bible quiz",
    caption: "The man who wrote two books of the New Testament was a doctor.",
    tags: ["luke", "biblequiz", "bibletrivia", "bible", "shorts"],
  },
  '2026-11-25': {
    hook: "He told them how long exile would last.",
    prompt: "How many years did Jeremiah say the exile in Babylon would last?",
    options: ["Forty", "Seventy", "Seven", "A hundred"],
    answerIndex: 1,
    teach: "Years later Daniel reads that number and works out the time is nearly up.",
    reference: "Jeremiah 29:10",
    ask: "Did you get it?",
    title: "How long was the exile in Babylon? Bible quiz",
    caption: "A number one prophet wrote and another prophet counted.",
    tags: ["jeremiah", "biblequiz", "bibletrivia", "daniel", "shorts"],
  },
  '2026-11-26': {
    hook: "They didn’t recognise Him all afternoon.",
    prompt: "On which road did two disciples walk with the risen Jesus without knowing Him?",
    options: ["The road to Damascus", "The road to Jericho", "The road to Gaza", "The road to Emmaus"],
    answerIndex: 3,
    teach: "They knew Him when He broke the bread at the table.",
    reference: "Luke 24:13",
    ask: "Did you get it?",
    title: "Which road did Jesus walk after rising? Bible quiz",
    caption: "Happy Thanksgiving. They knew Him at the table.",
    tags: ["emmaus", "thanksgiving", "biblequiz", "bibletrivia", "shorts"],
  },
  '2026-11-27': {
    hook: "Three days before she risked her life.",
    prompt: "What did Esther ask her people to do for three days before she saw the king?",
    options: ["March", "Fast", "Leave the city", "Send letters"],
    answerIndex: 1,
    teach: "Going in unsummoned could cost her life. \"If I perish, I perish.\"",
    reference: "Esther 4:16",
    ask: "Did you know that one?",
    title: "What did Esther do before seeing the king? Bible quiz",
    caption: "She asked for three days before she walked in.",
    tags: ["esther", "biblequiz", "bibletrivia", "courage", "shorts"],
  },
  '2026-11-28': {
    hook: "His first miracle was at a party.",
    prompt: "What was Jesus’ first sign in John’s gospel?",
    options: ["Feeding five thousand", "Healing a blind man", "Water into wine", "Walking on water"],
    answerIndex: 2,
    teach: "Six stone jars at a wedding in Cana, and the best wine saved for last.",
    reference: "John 2:11",
    ask: "Did you get it?",
    title: "What was Jesus’ first miracle? Bible quiz",
    caption: "It happened at a wedding that ran out of wine.",
    tags: ["cana", "biblequiz", "john", "bibletrivia", "shorts"],
  },
  '2026-11-29': {
    hook: "The lowest servant’s job.",
    prompt: "At the last supper, what did Jesus do that was the lowest servant’s job?",
    options: ["Anointed their heads", "Washed their feet", "Served the bread", "Lit the lamps"],
    answerIndex: 1,
    teach: "Peter tried to refuse it.",
    reference: "John 13:5",
    ask: "Did you get it?",
    title: "What did Jesus do at the last supper? Bible quiz",
    caption: "The job nobody at the table wanted.",
    tags: ["lastsupper", "biblequiz", "john", "bibletrivia", "shorts"],
  },
  '2026-11-30': {
    hook: "Acts has no ending.",
    prompt: "How does the book of Acts end?",
    options: ["With Paul’s execution", "With Peter’s release", "With Paul in Rome, welcoming everyone", "With the fall of Jerusalem"],
    answerIndex: 2,
    teach: "Two whole years under guard, welcoming everyone who came. Then it stops.",
    reference: "Acts 28:31",
    ask: "Did you know that?",
    title: "How does the book of Acts end? Bible quiz",
    caption: "The story stops mid-sentence, on purpose.",
    tags: ["acts", "paul", "biblequiz", "bibletrivia", "shorts"],
  },
}

export const SCRIPTED_MYTH: Record<string, ScriptedMyth> = {
  '2026-11-01': {
    hook: 'The Red Sea didn\u2019t part in a second.',
    title: 'All that night', reference: 'Exodus 14:21', art: '/tiktok/stages/water.jpg',
    ask: 'How did you always picture it?',
    title_yt: 'Did the Red Sea part instantly? What Exodus actually says',
    caption: 'A strong east wind, all that night. They stood in the dark and watched it happen.',
    tags: ['redsea', 'moses', 'exodus', 'biblefacts', 'shorts'],
  },
  '2026-11-02': {
    hook: 'Jonah wasn’t swallowed by a whale.',
    title: 'A great fish', reference: 'Jonah 1:17', art: '/tiktok/exchange/why-are-you-afraid.jpg',
    ask: 'What do you think it was?',
    title_yt: 'Was Jonah swallowed by a whale? What the Bible actually says',
    caption: 'The book of Jonah never says whale. It says the Lord appointed a great fish.',
    tags: ['jonah', 'bible', 'biblefacts', 'faith', 'shorts'],
  },
  '2026-11-03': {
    hook: 'Matthew never says three wise men.',
    title: 'Into the house', reference: 'Matthew 2:11', art: '/tiktok/stages/house.jpg',
    ask: 'Which part did you grow up with?',
    title_yt: 'Were there really three wise men? What Matthew actually says',
    caption: 'Three gifts, no number of visitors, and they came into a house, not a stable.',
    tags: ['wisemen', 'christmas', 'biblefacts', 'matthew', 'shorts'],
  },
  '2026-11-04': {
    hook: 'That’s not what the verse says.',
    title: 'The love of money', reference: '1 Timothy 6:10', art: '/tiktok/stages/temple.jpg',
    ask: 'Which version did you learn?',
    title_yt: 'Is money the root of all evil? The verse everyone misquotes',
    caption: 'Paul wrote that the love of money is a root of all kinds of evil. One word changes the whole verse.',
    tags: ['money', 'bible', 'biblefacts', 'misquoted', 'shorts'],
  },
  '2026-11-05': {
    hook: 'Genesis never says it was an apple.',
    title: 'The fruit', reference: 'Genesis 3:6', art: '/tiktok/scenes/garden_night.jpg',
    ask: 'What did you always picture?',
    title_yt: 'Did Adam and Eve eat an apple? What Genesis actually says',
    caption: 'Genesis just says fruit. The apple came much later, mostly from paintings.',
    tags: ['genesis', 'adamandeve', 'biblefacts', 'bible', 'shorts'],
  },
  '2026-11-06': {
    hook: 'Noah didn’t take just two of every animal.',
    title: 'Seven pairs', reference: 'Genesis 7:2', art: '/tiktok/stages/water.jpg',
    ask: 'Did you know that one?',
    title_yt: 'Did Noah take two of every animal? Read the next chapter',
    caption: 'For the clean animals, God tells Noah to take seven pairs.',
    tags: ['noah', 'noahsark', 'biblefacts', 'genesis', 'shorts'],
  },
  '2026-11-07': {
    hook: 'This famous verse isn’t in the Bible.',
    title: 'God helps the ones who can’t', reference: 'Mark 1:41', art: '/tiktok/stages/road.jpg',
    ask: 'Where did you first hear it?',
    title_yt: 'Is "God helps those who help themselves" in the Bible? It isn\u2019t',
    caption: 'It sounds like scripture. It is an old proverb, and the Bible keeps saying the opposite.',
    tags: ['bible', 'biblefacts', 'misquoted', 'faith', 'shorts'],
  },
  '2026-11-08': {
    hook: 'There is no innkeeper in the Christmas story.',
    title: 'No room', reference: 'Luke 2:7', art: '/tiktok/verse/luke-2-11.jpg',
    ask: 'Did your church have one?',
    title_yt: 'Was there an innkeeper in the Christmas story? What Luke says',
    caption: 'Luke just says there was no room for them. Nobody slams a door.',
    tags: ['christmas', 'nativity', 'biblefacts', 'luke', 'shorts'],
  },
  '2026-11-09': {
    hook: "There was no horse.",
    title: "He fell to the ground", reference: "Acts 9:4", art: "/tiktok/stages/road.jpg",
    ask: "Did you picture one too?",
    title_yt: "Was Paul knocked off a horse? What Acts actually says",
    caption: "Acts just says he fell to the ground. Every painting added the horse.",
    tags: ["paul", "damascus", "biblefacts", "acts", "shorts"],
  },
  '2026-11-10': {
    hook: "The crowd was bigger than five thousand.",
    title: "Besides women and children", reference: "Matthew 14:21", art: "/tiktok/scenes/shore.jpg",
    ask: "How many do you think were there?",
    title_yt: "Did Jesus really feed 5,000 people? Read the verse",
    caption: "Matthew counts five thousand men, besides women and children.",
    tags: ["feedingthe5000", "biblefacts", "matthew", "bible", "shorts"],
  },
  '2026-11-11': {
    hook: "The mark of Cain was not a curse.",
    title: "The mark", reference: "Genesis 4:15", art: "/tiktok/scenes/harvest_field.jpg",
    ask: "Did you know that?",
    title_yt: "What was the mark of Cain? What Genesis actually says",
    caption: "God marked him so nobody would kill him. Protection, given to the first murderer.",
    tags: ["cain", "genesis", "biblefacts", "bible", "shorts"],
  },
  '2026-11-12': {
    hook: "Jonah wasn’t running from fear.",
    title: "He ran from mercy", reference: "Jonah 4:2", art: "/tiktok/scenes/city_gate.jpg",
    ask: "Did that surprise you?",
    title_yt: "Why did Jonah really run? He says it himself",
    caption: "He says he ran because he knew God was gracious and would forgive them.",
    tags: ["jonah", "biblefacts", "mercy", "bible", "shorts"],
  },
  '2026-11-13': {
    hook: "God didn’t rename Saul on the road.",
    title: "Also called Paul", reference: "Acts 13:9", art: "/tiktok/scenes/temple_court.jpg",
    ask: "Did you think it changed at his conversion?",
    title_yt: "When did Saul become Paul? Not when you think",
    caption: "He is still Saul for years. Then Luke just writes: Saul, who was also called Paul.",
    tags: ["paul", "acts", "biblefacts", "bible", "shorts"],
  },
  '2026-11-14': {
    hook: "Isaiah never says lion and lamb.",
    title: "The wolf and the lamb", reference: "Isaiah 11:6", art: "/tiktok/stages/hills.jpg",
    ask: "Which version did you learn?",
    title_yt: "Does the lion lie down with the lamb? Read Isaiah 11",
    caption: "The wolf lives with the lamb. The leopard lies down with the goat.",
    tags: ["isaiah", "misquoted", "biblefacts", "bible", "shorts"],
  },
  '2026-11-15': {
    hook: "That’s only half the verse.",
    title: "Before destruction", reference: "Proverbs 16:18", art: "/tiktok/scenes/throne_room.jpg",
    ask: "Did you know there were two halves?",
    title_yt: "Pride comes before a fall? Here is the full verse",
    caption: "Pride goes before destruction, a haughty spirit before a fall.",
    tags: ["proverbs", "pride", "misquoted", "biblefacts", "shorts"],
  },
  '2026-11-16': {
    hook: "Jesus never calls him good.",
    title: "A Samaritan", reference: "Luke 10:33", art: "/tiktok/stages/road.jpg",
    ask: "Did you know that one?",
    title_yt: "Was the Good Samaritan called good? Read Luke 10",
    caption: "The story just says a Samaritan. Good is a word we added.",
    tags: ["goodsamaritan", "luke", "biblefacts", "bible", "shorts"],
  },
  '2026-11-17': {
    hook: "Thomas was braver than his nickname.",
    title: "Let us also go", reference: "John 11:16", art: "/tiktok/stages/upper.jpg",
    ask: "Did you know Thomas said that?",
    title_yt: "Doubting Thomas said this first",
    caption: "When Jesus headed back toward danger, Thomas said: let us go too, that we may die with him.",
    tags: ["thomas", "john", "biblefacts", "bible", "shorts"],
  },
  '2026-11-18': {
    hook: "Noah wasn’t on the ark for forty days.",
    title: "About a year", reference: "Genesis 8:14", art: "/tiktok/stages/water.jpg",
    ask: "Which number did you grow up with?",
    title_yt: "How long was Noah on the ark? Not forty days",
    caption: "It rained forty days. They were on the ark about a year.",
    tags: ["noah", "noahsark", "genesis", "biblefacts", "shorts"],
  },
  '2026-11-19': {
    hook: "It wasn’t the tree of life.",
    title: "The other tree", reference: "Genesis 2:17", art: "/tiktok/scenes/garden_night.jpg",
    ask: "Did you mix them up too?",
    title_yt: "Which tree did Adam and Eve eat from?",
    caption: "The forbidden one was the tree of the knowledge of good and evil.",
    tags: ["genesis", "adamandeve", "biblefacts", "bible", "shorts"],
  },
  '2026-11-20': {
    hook: "This famous line isn’t in the Bible.",
    title: "Not in the Bible", reference: "Not in the Bible", art: "/tiktok/stages/house.jpg",
    ask: "Who used to say it to you?",
    title_yt: "Is cleanliness next to godliness in the Bible?",
    caption: "Not anywhere in it. An old saying that got attached to scripture.",
    tags: ["misquoted", "bible", "biblefacts", "faith", "shorts"],
  },
  '2026-11-21': {
    hook: "It sounds like Solomon. It isn’t.",
    title: "Not in the Bible", reference: "Not in the Bible", art: "/tiktok/scenes/wilderness_camp.jpg",
    ask: "What verse do you hold on to instead?",
    title_yt: "Is this too shall pass in the Bible?",
    caption: "It is an old Persian saying, not scripture.",
    tags: ["misquoted", "bible", "biblefacts", "faith", "shorts"],
  },
  '2026-11-22': {
    hook: "People quote it like scripture.",
    title: "Not in the Bible", reference: "Not in the Bible", art: "/tiktok/stages/upper.jpg",
    ask: "Did you think it was in the Bible?",
    title_yt: "Is to thine own self be true in the Bible?",
    caption: "It is Shakespeare. Hamlet.",
    tags: ["misquoted", "bible", "biblefacts", "shakespeare", "shorts"],
  },
  '2026-11-23': {
    hook: "Angels aren’t sweet little babies.",
    title: "Do not be afraid", reference: "Luke 2:10", art: "/tiktok/stages/field.jpg",
    ask: "What did you picture?",
    title_yt: "What do angels look like in the Bible?",
    caption: "Again and again, the first thing an angel says is: do not be afraid.",
    tags: ["angels", "christmas", "biblefacts", "bible", "shorts"],
  },
  '2026-11-24': {
    hook: "The Bible never says Elijah died.",
    title: "Taken up", reference: "2 Kings 2:11", art: "/tiktok/verse/2-kings-2-11.jpg",
    ask: "Did you know that?",
    title_yt: "Did Elijah die? What 2 Kings says",
    caption: "A chariot of fire, a whirlwind, and he was taken up.",
    tags: ["elijah", "biblefacts", "bible", "faith", "shorts"],
  },
  '2026-11-25': {
    hook: "Genesis 3 never uses that name.",
    title: "The serpent", reference: "Genesis 3:1", art: "/tiktok/stages/wilderness.jpg",
    ask: "Did you know that one?",
    title_yt: "Does Genesis 3 say Satan? Read it again",
    caption: "Genesis says the serpent. The connection is made later, in Revelation.",
    tags: ["genesis", "biblefacts", "bible", "faith", "shorts"],
  },
  '2026-11-26': {
    hook: "That’s not quite the verse.",
    title: "A way out", reference: "1 Corinthians 10:13", art: "/tiktok/scenes/mountain.jpg",
    ask: "Which version did you hear?",
    title_yt: "God won’t give you more than you can handle?",
    caption: "It is about temptation, and a way out. Happy Thanksgiving.",
    tags: ["misquoted", "biblefacts", "faith", "thanksgiving", "shorts"],
  },
  '2026-11-27': {
    hook: "The wise men didn’t go straight to Bethlehem.",
    title: "Jerusalem first", reference: "Matthew 2:1", art: "/tiktok/stages/gate.jpg",
    ask: "Did you know that part?",
    title_yt: "Did the wise men go straight to Bethlehem?",
    caption: "They went to Jerusalem first and asked Herod. That is how Herod found out.",
    tags: ["wisemen", "christmas", "matthew", "biblefacts", "shorts"],
  },
  '2026-11-28': {
    hook: "Luke never mentions a donkey.",
    title: "They went", reference: "Luke 2:5", art: "/tiktok/stages/road.jpg",
    ask: "Did yours have one?",
    title_yt: "Did Mary ride a donkey to Bethlehem?",
    caption: "Every Christmas card added it.",
    tags: ["christmas", "nativity", "luke", "biblefacts", "shorts"],
  },
  '2026-11-29': {
    hook: "The Bible never gives a date.",
    title: "No date", reference: "Luke 2:7", art: "/tiktok/verse/luke-2-11.jpg",
    ask: "Did you know that?",
    title_yt: "Was Jesus born on December 25? What the Bible says",
    caption: "Not a month, not a day. The date came centuries later.",
    tags: ["christmas", "advent", "biblefacts", "luke", "shorts"],
  },
  '2026-11-30': {
    hook: "The word prodigal isn’t in the story.",
    title: "A man had two sons", reference: "Luke 15:11", art: "/tiktok/stages/house.jpg",
    ask: "Did you know that?",
    title_yt: "Is the word prodigal in the Bible?",
    caption: "Jesus just says a man had two sons. It is as much about the older one.",
    tags: ["prodigalson", "luke", "biblefacts", "bible", "shorts"],
  },
}

export const scriptedQuiz = (date: string): ScriptedQuiz | null => SCRIPTED_QUIZ[date] ?? null
export const scriptedMyth = (date: string): ScriptedMyth | null => SCRIPTED_MYTH[date] ?? null

/**
 * The day both numbered series count from: Daily Bible Quiz No. 1 and Most
 * People Get This Wrong No. 1 both went out on 2026-11-01. One function, read
 * by the renderer (`make.ts`) AND the app's "Yesterday's board" card, so the
 * number on the video and the number in the app can never disagree.
 */
export const SCRIPTED_FROM = '2026-11-01'
export function scriptedNo(date: string): number {
  const day = (ymd: string) => Math.round(Date.parse(`${ymd}T00:00:00Z`) / 86_400_000)
  return day(date) - day(SCRIPTED_FROM) + 1
}
